import {
  Table,
  Pagination,
  SendMessageTooltip,
  EmptyContent,
  HeadItemType,
  Icon
} from 'components'
import { RankingItem } from 'services/home/interfaces'
import { useSelector, useDispatch } from 'store/store'
import { OfficeTypes } from '../GoalProgress'
import { officeColors } from '../GoalProgress/constants'
import { getRanking, setRankingPage } from 'store/HomeContext/homeSlice'
import { numberToDecimalString as toDecimal } from 'commons/utils/number'

import * as S from './styles'

interface RankingTableProps {
  quota: string
  selectedOffice: OfficeTypes
  selectedGroup: string | null
  screenshotComponentRef: React.MutableRefObject<null>
}

const RankingTable: React.FC<RankingTableProps> = ({
  quota,
  selectedOffice,
  selectedGroup,
  screenshotComponentRef
}) => {
  const dispatch = useDispatch()
  const {
    loadingRanking: loading,
    rankingData,
    rankingPag: { totalPages, totalItems, currentPage, pageSize }
  } = useSelector((state) => state.home)

  const starColors = ['#D9B926', '#8E8E8E', '#821B17']

  const getZoneUser = (item: RankingItem) => {
    if (item.NomeUsuarioZona && item.EmailUsuarioZona) {
      return (
        <SendMessageTooltip
          name={item.NomeUsuarioZona}
          email={item.EmailUsuarioZona.split(';')[0]}
          phone={item.TelefoneUsuarioZona}
        >
          <span>{item.CodigoUsuarioZona ?? item.DescricaoZona}</span>
        </SendMessageTooltip>
      )
    }
    return <span>{item.DescricaoZona}</span>
  }

  const tableLabels = (): HeadItemType[] => {
    const tableHead: HeadItemType[] = [
      { label: 'Posição', columnName: 'Posicao' },
      { label: 'ID zona', columnName: 'Zona' },
      { label: 'Zona de venda', columnName: 'ZonaVenda' },
      { label: 'Meta total', columnName: 'MetaTotal' },
      { label: 'Vol. aprovado', columnName: 'VolumeAprovado' },
      { label: '% aprovada', columnName: 'PercentagemAprovada' }
    ]

    const showOffice = selectedOffice === 'TODOS'

    if (showOffice)
      tableHead.splice(1, 0, {
        label: 'Escritório',
        columnName: 'Escritorio'
      })
    if (!selectedGroup)
      tableHead.splice(showOffice ? 2 : 1, 0, {
        label: 'Regional',
        columnName: 'Descricao'
      })

    return tableHead
  }

  const tableData = () => {
    return rankingData.map((item) => {
      const rowContent = [
        <S.DataWrap key={`position-${item.Zona}`}>
          {item.Posicao <= 3 ? (
            <S.StarIcon sx={{ color: starColors[item.Posicao - 1] }} />
          ) : (
            <S.EmptyIcon />
          )}
          {item.Posicao}
        </S.DataWrap>,
        item.Zona,
        getZoneUser(item),
        <S.DataWrap key={`goal-${item.Zona}`}>
          <Icon icon="sandal" color={officeColors[selectedOffice]} size="16" />
          <span>{toDecimal(item.MetaTotal)}</span>
        </S.DataWrap>,
        <S.DataWrap key={`approved-${item.Zona}`} achieved={item.PercentualMeta >= 100}>
          <Icon icon="sandal" color={officeColors[selectedOffice]} size="16" />
          <span>{toDecimal(item.VolumeAprovado)}</span>
        </S.DataWrap>,
        <S.DataWrap key={`percent-${item.Zona}`} achieved={item.PercentualMeta >= 100}>
          <span>{Number(item.PercentualMeta.toFixed(2))} %</span>
        </S.DataWrap>
      ]
      const showOffice = selectedOffice === 'TODOS'
      if (showOffice) rowContent.splice(1, 0, item.Escritorio)
      if (!selectedGroup) rowContent.splice(showOffice ? 2 : 1, 0, item.DescricaoRegional)
      return {
        id: item.Zona,
        content: rowContent
      }
    })
  }

  const handlePageChange = (page: number) => {
    dispatch(setRankingPage(page))
    dispatch(
      getRanking(
        {
          AnoMesCota: quota,
          Escritorio: selectedOffice === 'TODOS' ? null : selectedOffice,
          Regional: selectedGroup,
          Gerente: null
        },
        { paginas: page - 1, linhas: 10 }
      )
    )
  }

  return !loading && !rankingData.length ? (
    <EmptyContent
      title="Nenhum resultado encontrado"
      description="Não foi possível encontrar dados para o período selecionado. Revise as opções selecionadas e tente novamente."
    />
  ) : (
    <>
      <Table
        headData={tableLabels()}
        bodyData={tableData()}
        rowHeight={48}
        tableRef={screenshotComponentRef}
      />
      <Pagination
        disabled={loading}
        numberOfPages={totalPages}
        currentPage={currentPage}
        numberOfItems={totalItems}
        itemsPerPage={pageSize}
        setCurrentPage={handlePageChange}
      />
    </>
  )
}

export default RankingTable
